import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const WaitlistModal = ({ isOpen, onClose, user, userProfile }) => {
  const [form, setForm] = useState({
    full_name: userProfile?.full_name || '',
    email: user?.email || '',
    social_handle: '',
    followers: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.full_name?.trim() || !form?.email?.trim() || !form?.social_handle?.trim() || !form?.followers) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: dbError } = await supabase?.from('card_waitlist')?.insert({
        user_id: user?.id,
        email: form?.email?.trim(),
        full_name: form?.full_name?.trim(),
        social_handle: form?.social_handle?.trim(),
        followers: parseInt(form?.followers, 10) || 0
      });
      if (dbError) throw dbError;
      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-2xl p-6 shadow-2xl"
        style={{ background: '#1E293B', border: '1px solid rgba(20,184,166,0.2)' }}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <Icon name="X" size={16} />
        </button>

        {success ? (
          <div className="text-center py-6">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(20, 184, 166, 0.15)', border: '1px solid rgba(20,184,166,0.3)' }}
            >
              <Icon name="CheckCircle" size={32} className="text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">You're on the list!</h3>
            <p className="text-muted-foreground text-sm">
              We'll notify you as soon as early access opens. High-performing creators get priority.
            </p>
            <button
              onClick={handleClose}
              className="mt-6 w-full py-3 rounded-xl font-semibold text-sm text-white transition-all duration-200 hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #0d9488, #1d4ed8)' }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-foreground">Join Early Access Waitlist</h3>
              <p className="text-muted-foreground text-sm mt-1">Be among the first to get the SocialCents Card.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wider">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={form?.full_name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all duration-200 focus:ring-2 focus:ring-primary/40"
                  style={{ background: '#0F172A', border: '1px solid #334155' }}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wider">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={form?.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all duration-200 focus:ring-2 focus:ring-primary/40"
                  style={{ background: '#0F172A', border: '1px solid #334155' }}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wider">Social Handle</label>
                <input
                  type="text"
                  name="social_handle"
                  value={form?.social_handle}
                  onChange={handleChange}
                  placeholder="@yourhandle"
                  className="w-full px-4 py-3 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all duration-200 focus:ring-2 focus:ring-primary/40"
                  style={{ background: '#0F172A', border: '1px solid #334155' }}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wider">Follower Count</label>
                <input
                  type="number"
                  name="followers"
                  value={form?.followers}
                  onChange={handleChange}
                  placeholder="e.g. 25000"
                  min="0"
                  className="w-full px-4 py-3 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all duration-200 focus:ring-2 focus:ring-primary/40"
                  style={{ background: '#0F172A', border: '1px solid #334155' }}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <Icon name="AlertCircle" size={14} className="text-error flex-shrink-0" />
                  <span className="text-error">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all duration-200 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #0d9488, #1d4ed8)' }}
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Joining...</>
                ) : (
                  <><Icon name="Sparkles" size={16} /> Join Waitlist</>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default WaitlistModal;
